import path from "path";
import { getOrCreateMainLogger } from "../logger/main";
import { MainWindow, registerNewWebContents, launchDevtools } from "./MainWindow";
import { mainBus } from "./MainBus";
import { windowsIndex } from "./mainStore";
import { iab } from "./IAB";
import { enableContextMenu } from "./contextMenu";
import { addWebContentsToWindowStream } from "./WindowEventStream";
import { ensureBoundsHaveIntegerValues } from "../util";
import { AnnotatedBrowserWindowOptions, WebContentsId } from "../types/types";
import { BrowserView, BrowserWindow, WebContents } from "electron";

const logger = getOrCreateMainLogger();

const getViewPreferences = (myOpts: AnnotatedBrowserWindowOptions): any => {
	const prefs = { ...myOpts.webPreferences };
	prefs.preload = path.join(__dirname, "e2o.js");

	return prefs;
};

/**
 * Set the bounds of a view
 * @param {MainProcessBusEvent} eventObj
 */
export const setViewBounds = (bounds: any, view: any): void => {
	const xMissing = !bounds.hasOwnProperty("x");
	const yMissing = !bounds.hasOwnProperty("y");
	const heightMissing = !bounds.hasOwnProperty("height");
	const widthMissing = !bounds.hasOwnProperty("width");

	if (xMissing || yMissing || widthMissing || heightMissing) {
		let errorMessage = "The following properties are missing from setViewBounds: ";
		if (xMissing) errorMessage = `${errorMessage}x, `;
		if (yMissing) errorMessage = `${errorMessage}y, `;
		if (widthMissing) errorMessage = `${errorMessage}width, `;
		if (heightMissing) errorMessage = `${errorMessage}height`;
		errorMessage = `${errorMessage}.`;
		logger.error(`${errorMessage} ${bounds} `);
		return;
	}

	// Electron will throw an exception if setBounds values are not whole numbers.
	bounds = ensureBoundsHaveIntegerValues(bounds);

	logger.debug(
		"Setting BrowserView Bounds",
		`ViewID: ${view.id}`,
		`Name: ${view.webContentsName}`,
		view.browserView.getBounds(),
		bounds
	);

	view.browserView.setBounds(bounds);
};

/**
 *
 * @param {number} windowWidth
 * @param {number} windowHeight
 * @param {number|undefined} titleBarHeight
 * @param {Object|undefined}params
 * @returns {{x: number, width: number, y: number, height: number}}
 */
export const calculateBrowserViewBounds = (
	windowWidth: number,
	windowHeight: number,
	titleBarHeight: number | undefined | null,
	overrideBounds: any
) => {
	let defaultBounds = {
		x: 0,
		y: titleBarHeight || 0,
		width: windowWidth,
		height: windowHeight - (titleBarHeight || 0),
	};

	let bounds = { ...defaultBounds };

	if (overrideBounds) {
		bounds.x = overrideBounds.x || overrideBounds.left || defaultBounds.x;
		bounds.y = overrideBounds.y || overrideBounds.top || defaultBounds.y;
		bounds.width = overrideBounds.width || defaultBounds.width;
		bounds.height = overrideBounds.height || defaultBounds.height;
	}

	return bounds;
};

const loadViewContent = async ({
	browserViews,
	myBV,
	url,
	webContentsName,
	overrideBounds,
}: {
	browserViews: BrowserViews;
	myBV: BrowserView;
	url: string;
	webContentsName: string;
	overrideBounds: any;
}) => {
	const { parentWindow } = browserViews;
	const { customData } = parentWindow.myOpts;

	logger.debug(`BrowserViews.loadViewContent`, url, webContentsName);

	const userAgentString =
		customData.window && customData.window.options ? customData.window.options.userAgentString || undefined : undefined;

	myBV.webContents.addListener("did-finish-load", () => {
		logger.debug(`BrowserViews.loadViewContent: did-finish-load`, url, userAgentString);
		/**
		 * Set the BrowserView bounds after loading the content: If a secondary monitor is bigger than the
		 * primary monitor and the window size is larger than the primary monitor, the browser view ends up bigger
		 * than the window.
		 *
		 * Use window bounds, as the window could have been resized while the content is loading.
		 *
		 * // Terry note: This block of content may be unnecessary because adjustToParent() is already being called
		 * in response to "resize" events which occur with every new window launch, but we'll leave it in place due to the
		 * comments above. Also note that the original code used BrowserWindow.getBounds() which at this point has incorrect
		 * bounds, whereas getContentBounds() has the correct size.
		 */
		const windowBounds = parentWindow.win.getContentBounds();
		const bounds = calculateBrowserViewBounds(
			windowBounds.width,
			windowBounds.height,
			parentWindow.titleBarHeight,
			overrideBounds
		);

		logger.debug(`BrowserViews.loadViewContent setBounds`, bounds);

		myBV.setBounds(ensureBoundsHaveIntegerValues(bounds));

		if (parentWindow.myOpts.contextMenu) {
			enableContextMenu(myBV.webContents, parentWindow.win);
		}

		mainBus.sendEvent(`systemEvent.browserView-content-loaded`, { viewId: myBV.webContents.id });
	});

	try {
		logger.debug(`BrowserViews.loadViewContent`, url, userAgentString);
		const params = userAgentString ? { userAgent: userAgentString } : {};
		await myBV.webContents.loadURL(url, params);
	} catch (err: any) {
		// Handle view urls that fail to load. While the did-fail-load event will trigger, because we await the loadURL
		// we still get an exception that must be handled.
		logger.error(
			`Failed to load url for view id: ${webContentsName} in window: ${parentWindow.windowName}. Message: ${err.message}`
		);
	}
};

type View = {
	id: WebContentsId;
	isClosing?: boolean;
	browserView: BrowserView;
	devtools: BrowserWindow | null;
	webContentsName: string;
};

export class BrowserViews {
	// If the parent window is a titleBar shell then there will be one and only one view
	// the "app content". It will always be view 0.
	views: Record<string, View> = {};

	parentWindow: MainWindow;

	constructor(parentWindow: MainWindow) {
		this.parentWindow = parentWindow;
	}

	/**
	 * Creates a BrowserView and attaches it to the window. Sets an initial size and loads content in that view.
	 *
	 * bounds: Starting size and position relative to the window. Defaults to the entire window excluding the titlebar space.
	 * allowAutoResize: default is true. enable/disable autoresize for view. Applied to width, height and horizontal (vertical is always false for the window titlebar)
	 *
	 * Note: Electron uses different os processes for creating, content loading and showing BrowserView. The WebContent's
	 * processId will change throughout these steps.
	 */
	async addBrowserView(params: {
		url: string;
		webContentsName?: string;
		allowAutoResize?: boolean;
		bounds?: any;
		visible?: boolean;
		closeWindowOnWebContentsClose?: boolean;
	}) {
		const { url, allowAutoResize = true, bounds, webContentsName: requestedWebContentsName, visible = true } = params;
		const { parentWindow } = this;
		logger.info(`BrowserViews.addBrowserView`, params);
		// viewId will always increase to prevent collision when adding and removing a view. This will probably need to be improved when
		// we add the ability to move views between windows.
		const myBV = new BrowserView({
			webPreferences: getViewPreferences(parentWindow.myOpts),
		});

		const webContentsId = myBV.webContents.id;

		// If no webContentsName was provided then derive it from the parent window
		const webContentsName = requestedWebContentsName ?? `${parentWindow.windowName}:view:${webContentsId}`;

		// NOTE: Do not call setBounds and setAutoResize before the BrowserView is added to the BrowserWindow.
		// Views must be attached to a window because view positioning is relative.
		if (visible) {
			parentWindow.win.addBrowserView(myBV);
		}
		registerNewWebContents(
			myBV.webContents,
			{
				id: webContentsId,
				manifest: parentWindow.manifest,
				isTitlebarWindow: false,
				windowName: parentWindow.windowName,
				webContentsName,
				isView: true,
				preloads: parentWindow.preloadList,
				contextIsolation: parentWindow.myOpts.webPreferences.contextIsolation,
			},
			parentWindow.myOpts
		);

		// Views are also put in the windowsIndex since IAB messages from these views will be
		// handled by their parent MainWindow (under the assumption that a view is within a
		// BrowserView window). This might need revision to better deal with random views such
		// as dashboard components that should be treated as individual windows.
		windowsIndex.byWebContentsId[myBV.webContents.id] = {
			webContentsName,
			webContents: myBV.webContents,
			mainWindow: parentWindow,
			browserView: myBV,
		};
		this.views[webContentsId] = {
			id: myBV.webContents.id,
			browserView: myBV,
			devtools: null,
			webContentsName,
		};

		if (visible) {
			myBV.setAutoResize({
				width: allowAutoResize,
				height: allowAutoResize,
				horizontal: allowAutoResize,
				vertical: false,
			});
		}

		addWebContentsToWindowStream(myBV.webContents, parentWindow);

		const loadParams = { browserViews: this, myBV, url, webContentsName, webContentsId, overrideBounds: bounds };
		if (parentWindow.domReady) {
			if (visible) {
				await loadViewContent(loadParams);
			} else {
				// don't wait content loading for hidden browserView
				loadViewContent(loadParams);
			}
		} else {
			parentWindow.win.webContents.once("dom-ready", () => {
				loadViewContent(loadParams);
			});
		}

		if (params.closeWindowOnWebContentsClose) {
			// @ts-ignore see https://github.com/electron/electron/issues/36245
			// imesner 20221103: Render processes that execute 'window.close()' within a BrowserView do not result in the window closing
			// See fiddle gist here, executed on v21.2.2: https://gist.github.com/mesner/56cdab123bd4cdd890641303b0d29afe.
			// I've not found any documented way of determining if a window in a browserview has programmatically called close
			// I've instead found an undocumented / private 'close' event on webcontents here: https://github.com/electron/electron/blob/main/shell/browser/api/electron_api_web_contents.cc#L1215
			myBV.webContents.addListener("close", (e) => {
				logger.info(
					`webcontents.close event fired for id: ${webContentsId} and url:${e?.sender?.getURL()}.  Closing main window per params.closeWindowOnWebContentsClose`
				);
				if (this.views[webContentsId]) {
					this.views[webContentsId].isClosing = true;
				}
				parentWindow.close();
			});
		}

		return webContentsId;
	}

	/**
	 * Adjusts the size of a browser view to match its parent window.
	 */
	adjustToParent(id: number = 0) {
		const { parentWindow } = this;
		const { titleBarHeight } = parentWindow;
		let keys = Object.keys(this.views);
		// If we have just a single BrowserView (e.g. we are a title bar shell)
		if (keys[id]) {
			let correctBounds = parentWindow.win.getContentBounds();
			this.views[keys[id]].browserView.setBounds(
				ensureBoundsHaveIntegerValues({
					x: 0,
					y: titleBarHeight,
					width: correctBounds.width,
					height: correctBounds.height - titleBarHeight,
				})
			);
		}
	}

	/**
	 * Shows the devtools for the given view id.
	 */
	showDeveloperTools(id: number): void {
		const view = this.views[id];
		if (!view) return;
		if (view.devtools) {
			view.devtools.focus();
			return;
		}
		const bw = launchDevtools(view.webContentsName, view.browserView.webContents, () => {
			view.devtools = null;
		});
		if (bw) view.devtools = bw;
	}

	/**
	 * Reloads all views. Returns true if there were any views to reload.
	 */
	reload(): boolean {
		logger.info(`BrowserViews.reload`);
		const keys = Object.keys(this.views);
		if (keys.length) {
			keys.forEach((viewId) => {
				const view = this.views[viewId];
				view.browserView.webContents.reload();
			});
			return true;
		}
		return false;
	}

	/**
	 * Set the bounds for a given view.
	 */
	setViewBounds(id: number, bounds: any): void {
		let view = this.views[id];
		if (!view || !bounds) {
			logger.warn(`setViewBounds() called with with improper viewId or bounds ${this.parentWindow.windowName}`);
			return;
		}
		setViewBounds(bounds, view);
	}

	/**
	 * Brings view to front of other views in a window
	 */
	bringViewToFront(id: number) {
		const { parentWindow } = this;
		let view = this.views[id];
		if (!view) {
			logger.warn(`bringViewToFront called with improper viewId ${parentWindow.windowName}`);
			return;
		}
		parentWindow.win.setTopBrowserView(view.browserView);
	}

	showBrowserView(id: number) {
		const { parentWindow } = this;
		let view = this.views[id];
		if (!view) {
			logger.warn(`showBrowserView called with improper viewId ${parentWindow.windowName}`);
			return;
		}
		parentWindow.win.addBrowserView(view.browserView);
	}

	hideBrowserView(id: number) {
		const { parentWindow } = this;
		let view = this.views[id];
		if (!view) {
			logger.warn(`hideBrowserView called with improper viewId ${parentWindow.windowName}`);
			return;
		}

		parentWindow.win.removeBrowserView(view.browserView);
	}

	/**
	 * Focuses a specific view. Returns true if successful.
	 */
	focus(id: number = 0): boolean {
		let keys = Object.keys(this.views);
		if (keys[id]) {
			//For now if the window only has one view focus on it, otherwise focus on the window
			this.views[keys[id]].browserView.webContents.focus();
			return true;
		}
		return false;
	}

	/**
	 * Sets the isClosing values for all views.
	 *
	 * @param isClosing Value indicating whether view is already closing.
	 */
	setAllIsClosing(isClosing: boolean) {
		Object.keys(this.views).forEach((id) => {
			this.views[id].isClosing = isClosing;
		});
	}

	/**
	 * Closes all BrowserViews. Resolves when all closed.
	 */
	async removeAll(): Promise<void> {
		logger.info(`BrowserViews.removeAll`);
		const views = Object.values(this.views);
		if (views.length) {
			const promises = views.map((view) => this.removeBrowserView(view.id));
			logger.info(`BrowserViews.removeAll`, promises);
			await Promise.all(promises);
		}
		logger.info(`BrowserViews.removeAll complete`);
	}

	/**
	 * Closes any active developer tools windows.
	 */
	closeCleanup() {
		Object.keys(this.views).forEach((viewId) => {
			const view = this.views[viewId];
			this.cleanupView(view);
		});
	}

	private cleanupView(view: View) {
		logger.info(`BrowserViews.cleanupView`, view.id, view?.browserView?.webContents?.getURL());
		iab.unsubscribeWebContentsId(view.id);
		mainBus.unsubscribeWebContentsId(view.id);
		delete windowsIndex.byWebContentsId[view.id];
		view.devtools?.close();

		/**
		 * Correctly releases resources before removing BrowserView from the list.
		 *
		 * Electron doesn't properly destroy BrowserViews when they are removed. This creates a memory leak
		 * unless they are manually destroyed with the undocumented internal Electron call WebContents.destroy().
		 */
		if (!view?.browserView?.webContents?.isDestroyed()) {
			try {
				(view?.browserView?.webContents as any)?.destroy();
			} catch (e: any) {
				logger.error(`Error calling webContents.destroy()`, e);
			}
		}
		delete this.views[view.id];
	}

	/**
	 * Closes the requested BrowserView and removes it.
	 * Returns true if it was removed successfully.
	 */
	async removeBrowserView(id: number): Promise<boolean> {
		const view = this.views[id];
		if (!view || !view.browserView || !view.browserView.webContents || view.browserView.webContents.isDestroyed()) {
			logger.warn(`removeBrowserView: No view with id ${id} or webcontents is null or destroyed`);
			return false;
		} else {
			logger.info(`removeBrowserView: called with id ${id}`);
		}

		const { webContents } = view.browserView;

		// if the view was already closing (from window.close() within its webcontents)
		// this executeJavaScript never resolves
		if (!view.isClosing) {
			/**
			 * Note that without this code, the closeFn() fails for the view and stops the window from closing.
			 * This is because the close was initialized but never completed. The window remains in a closing state,
			 * which freezes it.
			 */
			await webContents.executeJavaScript('window.dispatchEvent(new Event("beforeunload"))');
		}
		webContents.removeAllListeners();
		this.parentWindow.win.removeBrowserView(view.browserView);

		this.cleanupView(view);

		return true;
	}

	private getDefaultWebContents(): WebContents | null {
		const keys = Object.keys(this.views);
		const id = keys[0];
		const view = this.views[id];
		if (!view || !view.browserView || !view.browserView.webContents || view.browserView.webContents.isDestroyed()) {
			return null;
		}

		const { webContents } = view.browserView;
		return webContents;
	}

	setZoomFactor(zoomFactor: number): void {
		const webContents = this.getDefaultWebContents();
		if (webContents === null) {
			logger.warn(`setZoomFactor: No view, or webContents is null or destroyed`);
		} else {
			logger.info(`setZoomFactor: called with id ${webContents.id} and zoomFactor ${zoomFactor}`);
			webContents.setZoomFactor(zoomFactor);
		}
	}

	getZoomFactor(): number {
		const webContents = this.getDefaultWebContents();
		if (webContents === null) {
			logger.warn(`getZoomLevel: No view, or webContents is null or destroyed`);
			return Number.NaN;
		} else {
			logger.info(`getZoomLevel: called with id ${webContents.id}`);
			return webContents.getZoomFactor();
		}
	}

	setZoomLevel(zoomLevel: number): void {
		const webContents = this.getDefaultWebContents();
		if (webContents === null) {
			logger.warn(`setZoomLevel: No view, or webContents is null or destroyed`);
		} else {
			logger.info(`setZoomLevel: called with id ${webContents.id} and zoomLevel ${zoomLevel}`);
			webContents.setZoomLevel(zoomLevel);
		}
	}

	getZoomLevel(): number {
		const webContents = this.getDefaultWebContents();
		if (webContents === null) {
			logger.warn(`getZoomLevel: No view, or webContents is null or destroyed`);
			return Number.NaN;
		} else {
			logger.info(`getZoomLevel: called with id ${webContents.id}`);
			return webContents.getZoomLevel();
		}
	}
}
